import {noop} from '@benchling/js-utils';
import {Accordion, Box, createStyles, Stack, Text} from '@mantine/core';
import React from 'react';

import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ExperimentalInputs} from 'procedures/planning/ProcedurePlanTypes';
import getProcedureUIString, {ProceduresStringKeys} from 'procedures/ProceduresStringKeys';
import {TransposedReplicateTable} from 'studies/typed/shared/procedure-runs/conditions/TransposedReplicateTable';
import {summarizeConditionGroups} from 'studies/typed/shared/procedure-runs/conditions/transposedReplicateTableRows';
import {ConditionGroup} from 'studies/typed/shared/procedure-runs/design-state/useExperimentDesignState';
import MethodExecutionLimitError from 'studies/typed/shared/procedure-runs/execution-status/MethodExecutionLimitError';
import {MethodExecutionLimitState} from 'studies/typed/shared/procedure-runs/execution-status/useMethodExecutionLimitState';
import getTestProps from 'test/lib/cypress/getTestProps';

export interface UnitOperationsSectionProps {
  conditions: Array<ConditionGroup>;
  methods: ReadonlyArray<{id: string; name: string}>;
  procedureType: ProcedureType;
  methodExecutionLimitState: MethodExecutionLimitState;
  experimentalInputs: ExperimentalInputs;
}

interface UnitOperation {
  methodId: string;
  methodName: string;
  conditionGroups: Array<ConditionGroup>;
}

/** A group is shown on a method only when that method id is listed in its coverage map. */
function coversMethod(group: ConditionGroup, methodId: string): boolean {
  return methodId in group.conditionIdsByPmdvId;
}

export default function UnitOperationsSection(props: UnitOperationsSectionProps): JSX.Element {
  const {classes} = useStyles();
  const title = getProcedureUIString(ProceduresStringKeys.METHOD, props.procedureType, {plural: true});
  const unitOperations = props.methods.map((method) => ({
    methodId: method.id,
    methodName: method.name,
    conditionGroups: props.conditions.filter((group) => coversMethod(group, method.id)),
  }));

  return (
    <Stack spacing="md" {...getTestProps({component: 'UnitOperationsSection', element: 'root'})}>
      <Text className={classes.title}>{title}</Text>
      <Accordion
        chevronPosition="left"
        classNames={{
          chevron: classes.chevron,
          content: classes.content,
          control: classes.control,
          item: classes.item,
        }}
        multiple={true}
        radius="sm"
        variant="separated"
      >
        {unitOperations.map((unitOperation) => (
          <UnitOperationAccordionItem
            key={unitOperation.methodId}
            experimentalInputs={props.experimentalInputs}
            procedureType={props.procedureType}
            unitOperation={unitOperation}
          />
        ))}
      </Accordion>
      {props.methodExecutionLimitState.isOverLimit && (
        <Box mt="md">
          <MethodExecutionLimitError
            replicateLimit={props.methodExecutionLimitState.replicateLimit}
            procedureType={props.procedureType}
          />
        </Box>
      )}
    </Stack>
  );
}

function UnitOperationAccordionItem({
  experimentalInputs,
  procedureType,
  unitOperation,
}: {
  experimentalInputs: ExperimentalInputs;
  procedureType: ProcedureType;
  unitOperation: UnitOperation;
}): JSX.Element {
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes} = useStyles();
  const summary = summarizeConditionGroups(unitOperation.conditionGroups, procedureType);

  return (
    <Accordion.Item
      value={unitOperation.methodId}
      {...getTestProps({
        component: 'UnitOperationsSection',
        element: 'unitOperation',
        key: unitOperation.methodId,
      })}
      data-amp-safe={false}
    >
      <Accordion.Control>
        <Stack spacing={0}>
          <Text className={classes.methodName}>{unitOperation.methodName}</Text>
          <Text className={classes.summary}>{summary}</Text>
        </Stack>
      </Accordion.Control>
      <Accordion.Panel>
        <UnitOperationConditionsTable
          experimentalInputs={experimentalInputs}
          procedureType={procedureType}
          unitOperation={unitOperation}
        />
      </Accordion.Panel>
    </Accordion.Item>
  );
}

function UnitOperationConditionsTable({
  experimentalInputs,
  procedureType,
  unitOperation,
}: {
  experimentalInputs: ExperimentalInputs;
  procedureType: ProcedureType;
  unitOperation: UnitOperation;
}): JSX.Element {
  return (
    <TransposedReplicateTable
      cid={`doe-replicates-${unitOperation.methodId}`}
      conditions={unitOperation.conditionGroups}
      experimentalInputs={experimentalInputs}
      hideFooter={true}
      isPlan={true}
      locked={true}
      procedureType={procedureType}
      setConditions={noop}
      setExperimentalInputs={noop}
    />
  );
}

const useStyles = createStyles((theme) => ({
  chevron: {
    color: theme.colors.gray[5],
    marginTop: theme.spacing.md,
  },
  content: {
    padding: 0,
    // The card border is the table's outer edge.
    '& .mediocre-tableEditable-scrollArea': {
      backgroundColor: 'transparent',
      overflowX: 'hidden',
    },
    '& .mediocre-tableEditable-scrollAreaWrapper': {
      borderLeft: 'none',
      borderRight: 'none',
    },
    '& .mediocre-tableEditable-table': {
      // Column widths are an inline pixel sum. min-width fills the card, and fixed layout shares that width.
      minWidth: '100%',
      '& td:first-child': {
        borderLeft: 'none',
      },
      '& td:last-child': {
        borderRight: 'none',
      },
    },
  },
  control: {
    alignItems: 'flex-start',
  },
  item: {
    backgroundColor: theme.white,
    border: `1px solid ${theme.colors.gray[2]}`,
    overflow: 'hidden',
    '&[data-active]': {
      backgroundColor: theme.white,
      borderColor: theme.colors.gray[2],
    },
  },
  methodName: {
    color: theme.colors.gray[9],
    fontSize: theme.fontSizes.md,
    fontWeight: theme.other.fontWeights.semibold,
    letterSpacing: 0,
    lineHeight: '16px',
  },
  summary: {
    color: theme.colors.gray[5],
    fontSize: theme.fontSizes.md,
    fontWeight: theme.other.fontWeights.normal,
    letterSpacing: 0,
    lineHeight: '16px',
  },
  title: {
    color: theme.black,
    fontSize: theme.fontSizes.md,
    fontWeight: theme.other.fontWeights.semibold,
    lineHeight: '20px',
    marginTop: theme.spacing.md,
  },
}));
